/*
  bartesian-lazy-video.js
  Muted, looping background videos that load lazily (bundles + closer sections).
  Paired with snippets/section-cocktail-makers-lazy-video.liquid.

  Markup contract:
  - video[data-lazy-video]     sources carry data-src; nothing downloads until near the viewport
  - [data-video-scope]         wrapper that holds the video and its toggle
  - [data-video-toggle]        pause/play button (hidden until the video is actually playing)

  Behaviour:
  - Poster shows first. Sources attach when the video is within 200px of the viewport.
  - Plays muted and loops; pauses when scrolled out of view, resumes when back.
  - Poster only (never loads the video) on Save-Data or 2G connections.
  - Reduced motion: poster only, the video isn't downloaded.
  - Pause button for accessibility (moving content longer than 5s needs a way to stop it).
*/
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || {};
  const lowData = connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '');

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (reduceMotion.matches && video.dataset.lazyLoaded !== 'true') return; // poster only
            load(video);
            play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, { rootMargin: '200px 0px' })
    : null;

  function load(video) {
    if (video.dataset.lazyLoaded === 'true') return;
    video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
    video.muted = true; // required for autoplay
    video.load();
    video.dataset.lazyLoaded = 'true';
  }

  function play(video) {
    if (reduceMotion.matches || video.dataset.userPaused === 'true') return;
    const attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(() => { /* blocked or missing source: poster stays */ });
  }

  function initLazyVideo(video) {
    if (video.dataset.lazyInit === 'true') return;
    video.dataset.lazyInit = 'true';
    if (lowData) return; // poster only

    const toggle = video.closest('[data-video-scope]')?.querySelector('[data-video-toggle]');

    video.addEventListener('playing', () => { if (toggle) toggle.hidden = false; });
    video.addEventListener('error', () => { if (toggle) toggle.hidden = true; }, true);

    toggle?.addEventListener('click', () => {
      if (video.paused) {
        delete video.dataset.userPaused;
        play(video);
        toggle.setAttribute('aria-pressed', 'false');
        toggle.setAttribute('aria-label', 'Pause background video');
      } else {
        video.dataset.userPaused = 'true';
        video.pause();
        toggle.setAttribute('aria-pressed', 'true');
        toggle.setAttribute('aria-label', 'Play background video');
      }
    });

    if (observer) {
      observer.observe(video);
    } else {
      load(video);
      play(video);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('video[data-lazy-video]').forEach(initLazyVideo);
  }

  reduceMotion.addEventListener?.('change', e => {
    if (e.matches) document.querySelectorAll('video[data-lazy-video]').forEach(video => video.pause());
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', e => initAll(e.target));
  document.addEventListener('shopify:section:unload', e => {
    e.target.querySelectorAll?.('video[data-lazy-video]').forEach(video => observer?.unobserve(video));
  });

  window.BartesianLazyVideo = { init: initLazyVideo, initAll };
})();
